/**
 * Bundle versions written by the local device-run scripts
 * (`scripts/cap-run-stg.ts`) start with this prefix. Such a build runs the
 * developer's checkout and must not be replaced by the env's deployed bundle.
 */
export const DEV_BUILD_VERSION_PREFIX = "dev-";

export interface UpdateDecisionInput {
  /** Latest store build number from Firebase Remote Config. */
  latestBuildNumber: number | undefined;
  /** The installed native build number. */
  currentBuildNumber: number;
  /** `version` of the `/config.json` at `capacitorAppUrl`. */
  latestVersion: string | undefined;
  /** `version` of the running bundle's `/config.json`. */
  currentVersion: string | undefined;
  /** `VITE_DEPLOY_ENV` of the `/config.json` at `capacitorAppUrl`. */
  latestDeployEnv: string | undefined;
  /** `VITE_DEPLOY_ENV` of the running bundle's `/config.json`. */
  currentDeployEnv: string | undefined;
}

export interface UpdateDecision {
  needStoreUpdate: boolean;
  needOTAUpdate: boolean;
  needOTAClean: boolean;
}

export function decideUpdate({
  latestBuildNumber,
  currentBuildNumber,
  latestVersion,
  currentVersion,
  latestDeployEnv,
  currentDeployEnv,
}: UpdateDecisionInput): UpdateDecision {
  const decision: UpdateDecision = {
    needStoreUpdate: false,
    needOTAUpdate: false,
    needOTAClean: false,
  };
  if (!latestBuildNumber) return decision;
  // we received build number from firebase, checking if update from store is required
  if (currentBuildNumber < latestBuildNumber) {
    decision.needStoreUpdate = true;
    return decision;
  }
  // Skip the OTA-update path when either side is missing version —
  // happens during the Phase-8 rollout window. Treat as "in-sync"
  // rather than triggering false positives. A dev build keeps its own
  // bundle as well. A differing version or (when both are known) a
  // differing deploy env triggers the update: a store build pointed at
  // another env must switch to that env's bundle even on the same commit.
  if (
    latestVersion &&
    currentVersion &&
    !currentVersion.startsWith(DEV_BUILD_VERSION_PREFIX) &&
    (latestVersion !== currentVersion ||
      (latestDeployEnv &&
        currentDeployEnv &&
        latestDeployEnv !== currentDeployEnv))
  ) {
    decision.needOTAUpdate = true;
  } else {
    decision.needOTAClean = true;
  }
  return decision;
}
